import React, { useRef, useEffect } from 'react';

const SmPCChangesGuide = ({ isOpen, onClose }) => {
  const overlayRef = useRef(null);
  const contentRef = useRef(null);

  // Handle clicking outside the popup and background scrolling
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the overlay ref exists and the click is outside the content
      if (
        overlayRef.current &&
        contentRef.current &&
        !contentRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    // Add event listener when popup is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent background scrolling when popup is open
      document.body.style.overflow = 'hidden';
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // If not open, return null
  if (!isOpen) return null;

  // Timeline point colors are derived from SmPCChanges.css (or standard Bootstrap colors)
  const pointStartColor = '#28a745'; // Green
  const pointChangeColor = '#ffc107'; // Yellow
  const pointLastColor = '#007bff'; // Blue (New)
  const pointNoChangeColor = '#bdc3c7'; // Grey
  const pointMissingColor = '#dc3545'; // Red
  const addedDiffColor = '#e6ffe6';
  const removedDiffColor = '#ffe6e6';

  return (
    <div
      ref={overlayRef}
      className="docshark-search-guide-overlay"
    >
      <div
        ref={contentRef}
        className="docshark-search-guide"
      >
        <div className="search-guide-header">
          <h2>Explanation: SmPC Text Changes</h2>
          <button
            className="close-button"
            aria-label="Close"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className="search-guide-content">
          <div className="search-section">
            <p>
              <strong>Status:</strong> This feature is experimental and under development, it may contain inaccuracies.
            </p>
          </div>
          <p>
            This feature provides a historical overview of changes to the Summary of Product Characteristics (SmPC) text for each medicine presentation since its initial authorisation.
            It helps track when and where significant text revisions were implemented over time.
          </p>

          <div className="search-section">
            <h3>Data Source</h3>
            <p>
              The source of all SmPCs is the <strong>EC Community Register</strong> (
              <a href="https://ec.europa.eu/health/documents/community-register/html/" target="_blank" rel="noopener noreferrer">EC Register link</a>).
              All available English SmPC documents are taken <strong>as is</strong> from this register.
            </p>
          </div>

          <div className="search-section">
            <h3>SmPC text changes since authorisation (per presentation)</h3>
            <p>
              This provides a precise, line-by-line comparison of the text content between documents where a change was detected.
            </p>
            <ul>
              <li>
                Text highlighted with a <span className="removed">red background</span> has been removed from the text.
              </li>
              <li>
                Text highlighted with a <span className="added">green background</span> has been added to the text.
              </li>
            </ul>
          </div>

          <div className="search-section">
            <h3>Timeline Overview</h3>
            <p>
              The <strong>SmPC section changes timeline</strong> visually maps the history of each SmPC section across multiple documents for a given product presentation.
            </p>
            <ul>
              <li>
                <span style={{color: pointStartColor, fontWeight: 'bold'}}>First SmPC:</span> Represents the earliest available SmPC document, typically from the time of initial authorisation.
              </li>
              <li>
                <span style={{color: pointLastColor, fontWeight: 'bold'}}>Last SmPC:</span> Indicates the most recently published SmPC document.
              </li>
              <li>
                <span style={{color: pointChangeColor, fontWeight: 'bold'}}>Text change:</span> A revision or alteration was detected in the text content of that specific SmPC section compared to the previous version.
              </li>
              <li>
                <span style={{color: pointNoChangeColor, fontWeight: 'bold'}}>No text change:</span> The document was re-published (e.g., for administrative reasons), but no change was detected in the specific section's text.
              </li>
              <li>
                <span style={{color: pointMissingColor, fontWeight: 'bold'}}>Missing data:</span> Data for this time point or section could not be retrieved from the source documents.
              </li>
            </ul>
            <p>
              Use the <strong>Filter by Sections</strong> controls to focus the timeline on specific SmPC sections (e.g., 4.2 Posology and Method of Administration).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmPCChangesGuide;